<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import Dialog from 'primevue/dialog';
import { useLaunchChecklistStore, type LaunchChecklistItem } from '@/stores/launchChecklistStore';
import RequiredPill from '@/components/launch-checklist/RequiredPill.vue';
import nymbleLogoWhite from '@/assets/images/onboarding/nymble-logo-white.svg';
import mascotWatermark from '@/assets/images/onboarding/mascot-watermark-white.svg';

const router = useRouter();
const launchChecklist = useLaunchChecklistStore();

// Bound straight to the store's ref, as ProfileModal does, so the dialog and everything else that
// opens it share one piece of state.
const { modalVisible } = storeToRefs(launchChecklist);

/** The first thing still outstanding — where the primary action sends you. */
const nextItem = computed(() => launchChecklist.items.find((item) => !item.isComplete));

/** "Start setup" until something has been done, "Continue setup" once the org is part-way through. */
const primaryLabel = computed(() =>
    launchChecklist.items.some((item) => item.isComplete) ? 'Continue setup' : 'Start setup'
);

const go = (item: LaunchChecklistItem) => {
    launchChecklist.closeModal();
    router.push(item.to);
};

const startSetup = () => {
    if (nextItem.value) go(nextItem.value);
    else launchChecklist.closeModal();
};

const dismiss = () => {
    // Opening it at all counts as having seen it, so it is never auto-opened again.
    launchChecklist.markSeen();
    launchChecklist.closeModal();
};
</script>

<template>
    <Dialog
        v-model:visible="modalVisible"
        modal
        :draggable="false"
        :resizable="false"
        appendTo="body"
        :showHeader="false"
        :style="{ width: '700px' }"
        :breakpoints="{ '960px': '90vw', '640px': '100vw' }"
        :pt="{ content: { class: 'launch-checklist-dialog-content' } }"
        class="launch-checklist-dialog"
        aria-label="Set up the basics before you go live"
    >
        <div class="lc-band">
            <img :src="mascotWatermark" alt="" class="lc-watermark" />
            <div class="lc-band-header">
                <img :src="nymbleLogoWhite" alt="Nymble" class="lc-logo" />
                <button type="button" class="lc-close" aria-label="Close" @click="dismiss">
                    <i class="pi pi-times"></i>
                </button>
            </div>
            <h2 class="lc-title">Set up the basics before you go live</h2>
            <p class="lc-subtitle">
                Your journey with Nymble starts with a few basics for your catalog, your customers, and how orders
                work. Knock these out in any order — each is a quick setup.
            </p>
        </div>

        <div class="lc-grid">
            <p class="lc-group-label">IN THE MAIN NAV</p>
            <div class="lc-row">
                <button v-for="item in launchChecklist.navItems" :key="item.stepId" type="button" class="lc-item" @click="go(item)">
                    <span class="lc-item-icon-well" :class="{ 'lc-item-icon-well-done': item.isComplete }">
                        <i :class="item.icon"></i>
                    </span>
                    <span class="lc-item-label">{{ item.label }}</span>
                    <i v-if="item.isComplete" class="pi pi-check-circle lc-item-done" aria-label="Done"></i>
                    <i v-else class="pi pi-chevron-right lc-item-go" aria-hidden="true"></i>
                </button>
            </div>

            <p class="lc-group-label">IN SETTINGS</p>
            <div class="lc-row lc-row-wrap">
                <button
                    v-for="item in launchChecklist.settingsItems"
                    :key="item.stepId"
                    type="button"
                    class="lc-item"
                    @click="go(item)"
                >
                    <span class="lc-item-icon-well" :class="{ 'lc-item-icon-well-done': item.isComplete }">
                        <i :class="item.icon"></i>
                    </span>
                    <span class="lc-item-label">{{ item.label }}</span>
                    <i v-if="item.isComplete" class="pi pi-check-circle lc-item-done" aria-label="Done"></i>
                    <i v-else class="pi pi-chevron-right lc-item-go" aria-hidden="true"></i>
                </button>
            </div>
        </div>

        <div class="lc-footer">
            <div class="lc-note">
                <RequiredPill />
                <span>tags in the nav show what's left — pick these up anytime.</span>
            </div>
            <div class="lc-actions">
                <button type="button" class="lc-later" @click="dismiss">I'll do this later</button>
                <button type="button" class="lc-primary" @click="startSetup">{{ primaryLabel }}</button>
            </div>
        </div>
    </Dialog>
</template>

<style lang="scss">
// Unscoped: the dialog is teleported to body, so its own chrome cannot be reached from scoped CSS.
.launch-checklist-dialog {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 24px 64px rgba(10, 31, 51, 0.35);
}

.launch-checklist-dialog-content {
    padding: 0 !important;
    background: #ffffff;
}
</style>

<style lang="scss" scoped>
.lc-band {
    position: relative;
    background: #08334b;
    padding: 30px 36px 28px;
    overflow: hidden;
}

// Sits off the right edge of the band and is clipped by it, as in the design. The asset carries
// its own opacity, so it is not dimmed again here.
.lc-watermark {
    position: absolute;
    top: 16px;
    left: 526px;
    width: 200px;
    height: 229px;
    pointer-events: none;
}

.lc-band-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
}

.lc-logo {
    height: 36px;
    width: auto;
}

.lc-close {
    background: none;
    border: none;
    padding: 0;
    color: #ffffff;
    opacity: 0.75;
    cursor: pointer;
    font-size: 18px;
    line-height: 1;

    &:hover {
        opacity: 1;
    }
}

.lc-title {
    position: relative;
    margin: 12px 0 0;
    color: #ffffff;
    font-size: 24px;
    font-weight: 600;
}

.lc-subtitle {
    position: relative;
    margin: 12px 0 0;
    max-width: 418px;
    color: #c4dde7;
    font-size: 15.5px;
    line-height: 1.55;
}

.lc-grid {
    padding: 18px 36px 4px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.lc-group-label {
    margin: 4px 0 0;
    color: #9aa7b2;
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.6px;
}

.lc-row {
    display: flex;
    gap: 10px;
}

// The settings group has five items, so it wraps into rows of two with the last one half width.
.lc-row-wrap {
    flex-wrap: wrap;

    .lc-item {
        flex: 0 0 calc(50% - 5px);
    }
}

.lc-item {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1 1 0;
    min-width: 0;
    padding: 10px 12px;
    background: #f7f9fa;
    border: 1px solid #eef1f4;
    border-radius: 10px;
    cursor: pointer;
    text-align: left;
    transition: background-color 0.15s, border-color 0.15s;

    &:hover {
        background: #eef3f6;
        border-color: #d9e2e8;
    }

    &:focus-visible {
        outline: 2px solid #08334b;
        outline-offset: 2px;
    }
}

.lc-item-icon-well {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #fdf3df;
    color: #b07a1a;
    font-size: 15px;
    flex-shrink: 0;
}

.lc-item-icon-well-done {
    background: #e8f6ee;
    color: #1f8a4c;
}

.lc-item-label {
    flex: 1 1 0;
    min-width: 0;
    color: #22344a;
    font-size: 14px;
    font-weight: 600;
}

.lc-item-go {
    color: #9aa7b2;
    font-size: 12px;
    flex-shrink: 0;
}

.lc-item-done {
    color: #1f8a4c;
    font-size: 15px;
    flex-shrink: 0;
}

.lc-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    padding: 22px 36px 26px;
    margin-top: 14px;
    background: #fafbfc;
    border-top: 1px solid #f2f4f6;
}

.lc-note {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #8a97a5;
    font-size: 13px;
    line-height: 1.4;
    max-width: 280px;
}

.lc-actions {
    display: flex;
    align-items: center;
    gap: 18px;
    flex-shrink: 0;
}

.lc-later {
    background: none;
    border: none;
    padding: 0;
    color: #7b8894;
    font-size: 14.5px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        color: #56626d;
    }
}

.lc-primary {
    padding: 11px 22px;
    background: #08334b;
    border: none;
    border-radius: 8px;
    color: #ffffff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: #0b4362;
    }

    &:focus-visible {
        outline: 2px solid #08334b;
        outline-offset: 2px;
    }
}

@media (max-width: 640px) {
    .lc-watermark {
        display: none;
    }

    .lc-row {
        flex-wrap: wrap;
    }

    .lc-row .lc-item,
    .lc-row-wrap .lc-item {
        flex: 1 1 100%;
    }

    .lc-footer {
        flex-direction: column;
        align-items: flex-start;
    }
}
</style>
